import { useTranslation } from 'react-i18next';
import { useSyncQueue } from '@/hooks/useSyncQueue';
import { useOfflineDetection } from '@/hooks/useOfflineDetection';
import { useBackgroundSync } from '@/hooks/useBackgroundSync';
import { RefreshCw, CloudOff, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { ConflictResolutionDialog } from './ConflictResolutionDialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

export const SyncStatusIndicator = () => {
  const { t } = useTranslation();
  const { isOnline } = useOfflineDetection();
  const { 
    isSyncing, 
    pendingCount, 
    processSyncQueue,
    currentConflict,
    resolveConflict,
    skipConflict
  } = useSyncQueue();
  const { isBackgroundSyncSupported } = useBackgroundSync();

  return (
    <>
      {/* Conflict Resolution Dialog */}
      <ConflictResolutionDialog
        conflict={currentConflict}
        onResolve={resolveConflict}
        onCancel={skipConflict}
      />

      {/* Status Indicator */}
      {(isSyncing || pendingCount > 0) && (
        <div className="fixed bottom-4 left-4 z-50">
          <div
            className={cn(
              "flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium shadow-lg transition-all",
              isSyncing && "bg-primary text-primary-foreground",
              !isOnline && pendingCount > 0 && "bg-amber-500 text-white",
              isOnline && pendingCount > 0 && !isSyncing && "bg-secondary text-secondary-foreground"
            )}
          >
            {isSyncing ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                <span>{t('offline.syncing')}</span>
              </>
            ) : !isOnline && pendingCount > 0 ? (
              <>
                <CloudOff className="w-4 h-4" />
                <span>{t('offline.pendingChanges', { count: pendingCount })}</span>
                {isBackgroundSyncSupported && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Zap className="w-3 h-3 ml-1 opacity-70" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{t('offline.backgroundSyncEnabled')}</p>
                    </TooltipContent>
                  </Tooltip>
                )}
              </>
            ) : isOnline && pendingCount > 0 ? (
              <Button
                size="sm"
                variant="ghost"
                className="h-auto p-0 hover:bg-transparent"
                onClick={() => processSyncQueue()}
              >
                <RefreshCw className="w-4 h-4 mr-1" />
                <span>{t('offline.syncNow', { count: pendingCount })}</span>
              </Button>
            ) : null}
          </div>
        </div>
      )}
    </>
  );
};
